import type { DnsRecordListItemBase } from "../DnsRecordListBase/index.js";
import { DnsRecordListBase } from "../DnsRecordListBase/index.js";
import type { Ingress, IngressCommon } from "../Ingress/index.js";
import type { DnsSrvRecord, DnsZoneCommon } from "../DnsZone/index.js";
import { DnsZone } from "../DnsZone/index.js";
import { provideReact } from "../../react/index.js";
import type { DnsRecordSettingsData } from "../DnsRecordSettings/index.js";

export interface SrvRecordListItem extends DnsRecordListItemBase {
  domain: string;
  fqdn: string;
  port: number;
  weight?: number;
  priority?: number;
}

interface SrvUpdate {
  zone: DnsZone;
  update: DnsSrvRecord[];
}
export class DnsRecordSrvList extends DnsRecordListBase<SrvRecordListItem> {
  public constructor(
    ingress: IngressCommon,
    parentZone: DnsZoneCommon,
    items: SrvRecordListItem[],
  ) {
    super(items, parentZone, ingress);
  }

  private isEqual(a: SrvRecordListItem, b: SrvRecordListItem): boolean {
    const jsonA = JSON.stringify({ ...a, type: "unchanged" });
    const jsonB = JSON.stringify({ ...b, type: "unchanged" });
    return jsonA === jsonB;
  }

  public static getFromIngress = provideReact(
    async (ingress: Ingress, rootZone: DnsZone) => {
      const ingressDetailed = await ingress.getDetailed();
      const dnsZoneDetailed = await rootZone.getDetailed();
      const zones = await DnsZone.query(ingressDetailed.project, {
        domain: ingressDetailed.hostname,
      }).execute();
      const items: SrvRecordListItem[] = [];
      zones.items.forEach((i) =>
        (i.recordSet.srv.records ?? []).forEach((a) =>
          items.push({
            domain: i.domain,
            fqdn: a.fqdn,
            port: a.port,
            priority: a.priority,
            weight: a.weight,
            type: "unchanged",
          }),
        ),
      );
      return new DnsRecordSrvList(ingressDetailed, dnsZoneDetailed, items);
    },
  );

  public addItem(item: SrvRecordListItem) {
    this.items = [...this.getItems(), { ...item, type: "new" }];
  }
  public updateItem(oldItem: SrvRecordListItem, newItem: SrvRecordListItem) {
    if (oldItem.type === "new") {
      this.items = this.getItems().map((i) =>
        this.isEqual(oldItem, i) ? { ...newItem, type: "new" } : i,
      );
      return;
    }
    const index = this.items.findIndex((i) => this.isEqual(oldItem, i));
    this.deleteItem(oldItem);
    this.items.splice(index, 0, { ...newItem, type: "new" });
  }
  public deleteItem(item: SrvRecordListItem) {
    if (item.type === "new") {
      this.items = this.getItems().filter((i) => !this.isEqual(item, i));
      return;
    }
    this.items = this.getItems().map((i) =>
      this.isEqual(item, i) ? { ...i, type: "deleted" } : i,
    );
  }

  private getSrvZoneName(domain: string): string {
    const serviceRegex = /^_[a-zA-Z0-9]+$/;
    const protocolRegex = /^_(tcp|udp|tls)+$/;

    const prefix = this.getPrefix(domain);
    if (prefix === "") {
      return "";
    }
    const elements = prefix.split(".");
    const [service, protocol] = [elements[0], elements[1]];
    if (
      protocol &&
      protocolRegex.test(protocol) &&
      service &&
      serviceRegex.test(service)
    ) {
      return [service, protocol].join(".");
    }
    return "";
  }

  private resolveSrvUpdate(
    existing: DnsSrvRecord[],
    update: SrvRecordListItem,
  ): DnsSrvRecord[] {
    if (existing.length === 0) {
      return [
        {
          fqdn: update.fqdn,
          port: update.port,
          priority: update.priority,
          weight: update.weight,
        },
      ];
    }
    if (update.type === "new") {
      return [...existing, update];
    }
    if (update.type === "deleted") {
      return existing.filter(
        (i) =>
          !(
            i.fqdn === update.fqdn &&
            i.weight === update.weight &&
            i.port === update.port &&
            i.priority === update.priority
          ),
      );
    }
    return existing;
  }

  private async resolveItemUpdates() {
    const zones = await DnsZone.query(this.ingress.project, {
      domain: this.ingress.hostname,
    }).execute();
    const items = this.getItems(["unchanged"]);
    const record: Record<string, SrvUpdate> = {};

    for (const item of items) {
      const itemFromRecord = record[item.domain];
      if (itemFromRecord === undefined) {
        const zoneFromApi = zones.items.find((i) => i.domain === item.domain);
        if (!zoneFromApi) {
          const zoneName = this.getSrvZoneName(item.domain);
          if (zoneName === "") {
            continue;
          }
          const zone = await DnsZone.create(zoneName, this.parentZone.id);
          const update = [
            {
              fqdn: item.fqdn,
              port: item.port,
              priority: item.priority,
              weight: item.weight,
            },
          ];
          record[item.domain] = { update, zone };
          continue;
        }
        const u = this.resolveSrvUpdate(
          zoneFromApi.recordSet.srv.records ?? [],
          item,
        );
        record[item.domain] = { update: u, zone: zoneFromApi };
        continue;
      }
      const u = this.resolveSrvUpdate(itemFromRecord.update, item);
      record[item.domain] = { update: u, zone: itemFromRecord.zone };
    }
    return record;
  }

  public async saveItems(settings: DnsRecordSettingsData): Promise<void> {
    const record = await this.resolveItemUpdates();

    const keys = Object.keys(record);
    for (const key of keys) {
      const item = record[key];
      if (item === undefined) {
        continue;
      }
      await item.zone.setSrvRecord(item.update, settings);
    }
  }
}
